import { useState } from 'react'
import { BrowserRouter, Navigate, Routes, Route, Link } from 'react-router-dom'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import UserMenu from './components/UserMenu'
import Dashboard from './pages/Dashboard/Dashboard'
import Jobs from './pages/Jobs/Jobs'
import JobDetail from './pages/Jobs/JobDetail'
import ImportDocumentPage from './pages/Jobs/ImportDocumentPage'
import ECustomsPreview from './pages/Jobs/ECustomsPreview'
import AuthCallback from './pages/AuthCallback/AuthCallback'
import SentryTest from './pages/SentryTest'
import AutoJobsPage from './pages/Jobs/AutoJobsPage'
import DocumentTemplatesPage from './pages/Jobs/DocumentTemplatesPage'
import Accounts from './pages/Accounts/Accounts'
import SageBulkImport from './pages/Accounts/SageBulkImport'
import Login from './pages/Login/Login'
import POACheck from './pages/Portal/POACheck'
import InviteUser from './pages/Admin/InviteUser'
import AdminIndex from './pages/Admin/index'
import AdminPage from './components/AdminBackLink'
import AdminUsers from './pages/Admin/Users'
import PendingReview from './pages/Customs/POA/PendingReview'
import './App.css'
import Customers from './pages/Customers/Customers'
import CustomsIndex from './pages/Customs/index'
import POAIndex from './pages/Customs/POA/index'
import POADetail from './pages/Customs/POA/POADetail'
import POAEdit from './pages/Customs/POA/POAEdit'
import CommercialInvoiceIndex from './pages/Customs/CommercialInvoice/index'
import InvoiceUpload from './pages/Customs/CommercialInvoice/InvoiceUpload'
import InvoiceDetail from './pages/Customs/CommercialInvoice/InvoiceDetail'
import InvoiceTemplates from './pages/Customs/InvoiceTemplates'
import TemplateWizard from './pages/Customs/CommercialInvoice/TemplateWizard'
import HSCodeSearch from './pages/Customs/HSCodeSearch'
import VerifyPage from './pages/Customs/Verify/index'
import HSCodeInspectionLog from './pages/Customs/HSCodeInspectionLog'
import ClearanceErrorLog from './pages/Customs/ClearanceErrorLog'
import MonthlyAuditSample from './pages/Customs/MonthlyAuditSample'
import CustomsLettersIndex from './pages/Customs/CustomsLetters/index'
import NewCase from './pages/Customs/CustomsLetters/NewCase'
import CaseDetail from './pages/Customs/CustomsLetters/CaseDetail'
import ETSFCompliance from './pages/Customs/ETSFCompliance/index'
import ETSFRecordDetail from './pages/Customs/ETSFCompliance/RecordDetail'
import HSCodeRules from './pages/Customs/HSCodeRules/index'
import DailyChecklistNew from './pages/Customs/ETSFCompliance/DailyChecklistNew'
import DailyChecklistDetail from './pages/Customs/ETSFCompliance/DailyChecklistDetail'
import ParcelIssues from './pages/ParcelIssues/index'
import IssueDetail from './pages/ParcelIssues/IssueDetail'
import WarehouseList from './pages/Warehouse/WarehouseList'
import NewWarehouse from './pages/Warehouse/NewWarehouse'
import LoadingListsPage from './pages/Warehouse/LoadingLists/index'
import UploadLoadingList from './pages/Warehouse/LoadingLists/Upload'
import LoadingListDetail from './pages/Warehouse/LoadingLists/Detail'
import PlansPage from './pages/Warehouse/Plans/index'
import PlanDetail from './pages/Warehouse/Plans/Detail'
import DevanningsPage from './pages/Warehouse/Devannings/index'
import DevanningWorkflow from './pages/Warehouse/Devannings/Workflow'
import DevanningReport from './pages/Warehouse/Devannings/Report'
import DeliveryNotes from './pages/Warehouse/Devannings/Notes'
import InventoryPage from './pages/Warehouse/Inventory/index'
import InboundPage from './pages/Warehouse/Inbound/index'
import PlannedOutboundPage from './pages/Warehouse/PlannedOutbound/index'
import OutboundPage from './pages/Warehouse/Outbound/index'
import DeliveriesPage from './pages/Warehouse/Deliveries/index'
import SchedulePage from './pages/Warehouse/Schedule'
import DataIndex from './pages/Data/index'
import DataCustomers from './pages/Data/Customers'
import DataCarriers from './pages/Data/Carriers'
import DataPorts from './pages/Data/Ports'
import DataChargeCodes from './pages/Data/ChargeCodes'
import DataShippingTerms from './pages/Data/ShippingTerms'
import DataPaymentTerms from './pages/Data/PaymentTerms'
import DataVatRates from './pages/Data/VatRates'
import DataContainerTypes from './pages/Data/ContainerTypes'
import DataJobStatuses from './pages/Data/JobStatuses'
import GroupsIndex from './pages/Admin/Groups/index'
import GroupEdit from './pages/Admin/Groups/GroupEdit'
import Branches from './pages/Admin/Branches'
import CompanySettingsPage from './pages/Admin/CompanySettings'
import CiParserComparePage from './pages/Admin/CiParserCompare'
import DataAddressTypes from './pages/Data/AddressTypes'
import AuditLog from './pages/Admin/AuditLog'
import AdminSage from './pages/Admin/Sage'
import SageReferenceData from './pages/Admin/SageReferenceData'
import EcustomsSettings from './pages/Admin/EcustomsSettings'
import NaRegistry from './pages/Admin/NaRegistry'
import CustomsLookups from './pages/Admin/CustomsLookups'
import GoodsLocations from './pages/Admin/GoodsLocations'
import PricingAdmin from './pages/Admin/Pricing'
import SupplierPricing from './pages/Admin/SupplierPricing'
import CustomerDetail from './pages/Customers/Detail'


function RoleBasedHome() {
  const { userRoles, loading } = useAuth()
  if (loading) return null
  if (userRoles.includes('CLIENT')) return <Navigate to="/portal/poa" replace />
  return <ProtectedRoute><Dashboard /></ProtectedRoute>
}

function DataMenu() {
  const { userRoles } = useAuth()
  const [open, setOpen] = useState(false)
  // All internal roles can at least view lookup data (each page/RLS policy gates edit
  // rights individually) — only the external CLIENT portal role is excluded.
  if (userRoles.length === 0 || userRoles.includes('CLIENT')) return null
  // Supplier rates are cost prices -- only the roles RLS lets read them (ADMIN/ACCOUNTS/OPERATIONS)
  // get the link. Lives here, not in the ADMIN-only Admin menu, because ACCOUNTS edits them.
  const canSeeSupplierPricing = userRoles.some(r => ['ADMIN', 'ACCOUNTS', 'OPERATIONS'].includes(r))
  const items = [
    { to: '/data', label: 'Overview' },
    { to: '/data/customers', label: 'Address Book' },
    { to: '/data/carriers', label: 'Carriers' },
    { to: '/data/ports', label: 'Ports & Airports' },
    { to: '/data/charge-codes', label: 'Charge Codes' },
    { to: '/data/container-types', label: 'Container Types' },
    { to: '/data/shipping-terms', label: 'Shipping Terms' },
    { to: '/data/job-statuses', label: 'Job Statuses' },
    { to: '/data/payment-terms', label: 'Payment Terms' },
    { to: '/data/vat-rates', label: 'VAT Rates' },
    { to: '/data/address-types', label: 'Address Types' },
    ...(canSeeSupplierPricing ? [{ to: '/admin/supplier-pricing', label: 'Supplier Pricing' }] : []),
  ]
  // Z73.18 follow-up: NA Registry / Goods Locations moved here from the Admin dropdown --
  // ordinary customs staff maintain these day to day (registering a new customer, adding a
  // haulier's ETSF), so gating them behind ADMIN was wrong. Split out as their own "Customs"
  // group (rather than folded into `items` above) so staff can tell at a glance which entries
  // are customs/eCustoms-specific vs general business reference data -- same reasoning behind
  // prefixing each label "Customs — ".
  const customsItems = [
    { to: '/admin/na-registry', label: 'Customs — NA Registry' },
    { to: '/admin/goods-locations', label: 'Customs — Goods Locations' },
  ]
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white flex items-center gap-1"
      >
        Data
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 py-1">
            {items.map(item => (
              <Link key={item.to} to={item.to} onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                {item.label}
              </Link>
            ))}
            <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
            <div className="px-4 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Customs</div>
            {customsItems.map(item => (
              <Link key={item.to} to={item.to} onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                {item.label}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function AdminMenu() {
  const { userRoles } = useAuth()
  const [open, setOpen] = useState(false)
  // Address Types (the one item OPERATIONS could see) moved to the Data menu — every
  // remaining item here is ADMIN-only, so the whole menu is now ADMIN-only too (otherwise
  // OPERATIONS would see an "Admin" button that opens an empty dropdown).
  if (!userRoles.includes('ADMIN')) return null
  return (
    <div className="relative">
      {/* Clicking the "Admin" text goes to the overview page (/admin); the chevron opens the menu. */}
      <div className="text-gray-600 dark:text-gray-300 flex items-center gap-1">
        <Link to="/admin" onClick={() => setOpen(false)} className="hover:text-gray-900 dark:hover:text-white">
          Admin
        </Link>
        <button
          onClick={() => setOpen(o => !o)}
          aria-label="Admin menu"
          aria-expanded={open}
          className="hover:text-gray-900 dark:hover:text-white p-0.5"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20 py-1">
            {userRoles.includes('ADMIN') && (
              <>
                <Link to="/admin" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700">
                  Admin Home (all admin pages)
                </Link>
                <Link to="/admin/users" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Manage Users
                </Link>
                <Link to="/admin/groups" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  User Groups
                </Link>
                <Link to="/admin/branches" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Branches
                </Link>
                <Link to="/admin/company-settings" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Company Settings
                </Link>
                <Link to="/admin/ci-parser" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  CI PDF Parser
                </Link>
                <Link to="/admin/invite" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Invite User
                </Link>
                <Link to="/admin/audit-log" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Audit Log
                </Link>
                <Link to="/admin/sage" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Connect Sage
                </Link>
                <Link to="/admin/sage-reference-data" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Sage Reference Data
                </Link>
                <Link to="/admin/pricing" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Pricing
                </Link>
                {/* Pages that previously had an entry ONLY on the /admin overview page. */}
                <Link to="/admin/ecustoms" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  eCustoms (Descartes)
                </Link>
                <Link to="/admin/customs-lookups/transport_modes" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Customs — Transport Modes
                </Link>
                <Link to="/admin/customs-lookups/declaration_types" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Customs — Declaration Types
                </Link>
                <Link to="/admin/customs-lookups/declaration_categories" onClick={() => setOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Customs — Declaration Categories
                </Link>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function AppContent() {
  const [navOpen] = useState(false)
  void navOpen
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <nav className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            CY UK
          </h1>
          <div className="flex gap-4 items-center">
            <Link to="/" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              Dashboard
            </Link>
            <Link to="/jobs" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              Jobs
            </Link>
            <Link to="/customers" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              Companies
            </Link>
            <Link to="/customs" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              Customs
            </Link>
            <Link to="/parcel-issues" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              Parcels
            </Link>
            <Link to="/warehouse" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              Warehouse
            </Link>
            <Link to="/warehouse/schedule" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              Schedule
            </Link>
            <Link to="/accounts" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
              Accounts
            </Link>
            <DataMenu />
            <AdminMenu />
            <div className="border-l border-gray-300 dark:border-gray-600 pl-4 ml-4">
              <UserMenu />
            </div>
          </div>
        </div>
      </nav>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/_sentry-test" element={<SentryTest />} />
        <Route
          path="/"
          element={<RoleBasedHome />}
        />
        <Route
          path="/jobs"
          element={
            <ProtectedRoute>
              <Jobs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs/autojobs"
          element={
            <ProtectedRoute>
              <AutoJobsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs/autojobs/templates"
          element={
            <ProtectedRoute>
              <DocumentTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs/import-document"
          element={
            <ProtectedRoute>
              <ImportDocumentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs/:id"
          element={
            <ProtectedRoute>
              <JobDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs/:id/ecustoms"
          element={
            <ProtectedRoute>
              <ECustomsPreview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts"
          element={
            <ProtectedRoute requiredRole={['ADMIN', 'ACCOUNTS']}>
              <Accounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/accounts/sage-import"
          element={
            <ProtectedRoute requiredRole={['ADMIN', 'ACCOUNTS']}>
              <SageBulkImport />
            </ProtectedRoute>
          }
        />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
      <Route path="/customs" element={<ProtectedRoute><CustomsIndex /></ProtectedRoute>} />
      <Route path="/customs/poa" element={<ProtectedRoute><POAIndex /></ProtectedRoute>} />
      <Route path="/customs/poa/:id" element={<ProtectedRoute><POADetail /></ProtectedRoute>} />
      <Route path="/customs/poa/:id/edit" element={<ProtectedRoute><POAEdit /></ProtectedRoute>} />
      <Route path="/customs/invoices" element={<ProtectedRoute><CommercialInvoiceIndex /></ProtectedRoute>} />
      <Route path="/customs/invoices/upload" element={<ProtectedRoute><InvoiceUpload /></ProtectedRoute>} />
      <Route path="/customs/invoices/templates" element={<ProtectedRoute><InvoiceTemplates /></ProtectedRoute>} />
      <Route path="/customs/invoices/templates/new" element={<ProtectedRoute><TemplateWizard /></ProtectedRoute>} />
      <Route path="/customs/invoices/templates/:id/edit" element={<ProtectedRoute><TemplateWizard /></ProtectedRoute>} />
      <Route path="/customs/invoices/hs-search" element={<ProtectedRoute><HSCodeSearch /></ProtectedRoute>} />
      <Route path="/customs/verify" element={<ProtectedRoute><VerifyPage /></ProtectedRoute>} />
      <Route path="/customs/invoices/:id" element={<ProtectedRoute><InvoiceDetail /></ProtectedRoute>} />
      <Route path="/customs/hs-inspections" element={<ProtectedRoute><HSCodeInspectionLog /></ProtectedRoute>} />
      <Route path="/customs/hs-rules" element={<ProtectedRoute><HSCodeRules /></ProtectedRoute>} />
      <Route path="/customs/clearance-errors" element={<ProtectedRoute><ClearanceErrorLog /></ProtectedRoute>} />
      <Route path="/customs/monthly-audit" element={<ProtectedRoute><MonthlyAuditSample /></ProtectedRoute>} />
      <Route path="/customs/letters" element={<ProtectedRoute><CustomsLettersIndex /></ProtectedRoute>} />
      <Route path="/customs/letters/new" element={<ProtectedRoute><NewCase /></ProtectedRoute>} />
      <Route path="/customs/letters/:id" element={<ProtectedRoute><CaseDetail /></ProtectedRoute>} />
      <Route path="/customs/etsf" element={<ProtectedRoute><ETSFCompliance /></ProtectedRoute>} />
      <Route path="/customs/etsf/checklist/new" element={<ProtectedRoute><DailyChecklistNew /></ProtectedRoute>} />
      <Route path="/customs/etsf/checklist/:id" element={<ProtectedRoute><DailyChecklistDetail /></ProtectedRoute>} />
      <Route path="/customs/etsf/:id" element={<ProtectedRoute><ETSFRecordDetail /></ProtectedRoute>} />
      <Route path="/parcel-issues" element={<ProtectedRoute><ParcelIssues /></ProtectedRoute>} />
      <Route path="/parcel-issues/:id" element={<ProtectedRoute><IssueDetail /></ProtectedRoute>} />
      <Route path="/warehouse" element={<ProtectedRoute><WarehouseList /></ProtectedRoute>} />
      <Route path="/warehouse/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
      <Route path="/warehouse/new" element={<ProtectedRoute><NewWarehouse /></ProtectedRoute>} />
      <Route path="/warehouse/:id" element={<Navigate to="loading-lists" replace />} />
      <Route path="/warehouse/:id/loading-lists" element={<ProtectedRoute><LoadingListsPage /></ProtectedRoute>} />
      <Route path="/warehouse/:id/loading-lists/upload" element={<ProtectedRoute><UploadLoadingList /></ProtectedRoute>} />
      <Route path="/warehouse/:id/loading-lists/:lid" element={<ProtectedRoute><LoadingListDetail /></ProtectedRoute>} />
      <Route path="/warehouse/:id/plans" element={<ProtectedRoute><PlansPage /></ProtectedRoute>} />
      <Route path="/warehouse/:id/plans/:pid" element={<ProtectedRoute><PlanDetail /></ProtectedRoute>} />
      <Route path="/warehouse/:id/devannings" element={<ProtectedRoute><DevanningsPage /></ProtectedRoute>} />
      <Route path="/warehouse/:id/devannings/:did" element={<ProtectedRoute><DevanningWorkflow /></ProtectedRoute>} />
      <Route path="/warehouse/:id/devannings/:did/report" element={<ProtectedRoute><DevanningReport /></ProtectedRoute>} />
      <Route path="/warehouse/:id/devannings/:did/notes" element={<ProtectedRoute><DeliveryNotes /></ProtectedRoute>} />
      <Route path="/warehouse/:id/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
      <Route path="/warehouse/:id/inbound"          element={<ProtectedRoute><InboundPage /></ProtectedRoute>} />
      <Route path="/warehouse/:id/planned-outbound" element={<ProtectedRoute><PlannedOutboundPage /></ProtectedRoute>} />
      <Route path="/warehouse/:id/deliveries"       element={<ProtectedRoute><DeliveriesPage /></ProtectedRoute>} />
      <Route path="/warehouse/:id/outbound"         element={<ProtectedRoute><OutboundPage /></ProtectedRoute>} />
        <Route path="/data" element={<ProtectedRoute><DataIndex /></ProtectedRoute>} />
      <Route path="/data/customers" element={<ProtectedRoute><DataCustomers /></ProtectedRoute>} />
      <Route path="/data/carriers" element={<ProtectedRoute><DataCarriers /></ProtectedRoute>} />
      <Route path="/data/ports" element={<ProtectedRoute><DataPorts /></ProtectedRoute>} />
      <Route path="/data/charge-codes" element={<ProtectedRoute><DataChargeCodes /></ProtectedRoute>} />
      <Route path="/data/container-types" element={<ProtectedRoute><DataContainerTypes /></ProtectedRoute>} />
      <Route path="/data/shipping-terms" element={<ProtectedRoute><DataShippingTerms /></ProtectedRoute>} />
      <Route path="/data/job-statuses" element={<ProtectedRoute><DataJobStatuses /></ProtectedRoute>} />
      <Route path="/data/payment-terms" element={<ProtectedRoute><DataPaymentTerms /></ProtectedRoute>} />
      <Route path="/data/vat-rates" element={<ProtectedRoute><DataVatRates /></ProtectedRoute>} />
      <Route path="/data/address-types" element={<ProtectedRoute><DataAddressTypes /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminIndex /></ProtectedRoute>} />
        <Route path="/admin/reference" element={<Navigate to="/data" replace />} />
        <Route path="/admin/address-types" element={<Navigate to="/data/address-types" replace />} />
        <Route path="/admin/users" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><AdminUsers /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/invite" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><InviteUser /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/groups" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><GroupsIndex /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/groups/:id" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><GroupEdit /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/branches" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><Branches /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/company-settings" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><CompanySettingsPage /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/ci-parser" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><CiParserComparePage /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/audit-log" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><AuditLog /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/sage" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><AdminSage /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/sage-reference-data" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><SageReferenceData /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/ecustoms" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><EcustomsSettings /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/pricing" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><PricingAdmin /></AdminPage></ProtectedRoute>} />
        {/* Cost-side prices: ADMIN + ACCOUNTS edit, OPERATIONS read-only (RLS is the real gate -- 20261001b) */}
        <Route path="/admin/supplier-pricing" element={<ProtectedRoute requiredRole={['ADMIN', 'ACCOUNTS', 'OPERATIONS']}><SupplierPricing /></ProtectedRoute>} />
        {/* Z73.18 follow-up: NA Registry / Goods Locations open to any authenticated internal
            role (moved to the Data dropdown, same access level as Ports/Carriers) -- ordinary
            customs staff maintain these day to day. URLs deliberately kept as /admin/... so
            existing bookmarks/links keep working; only the nav placement and role gate changed. */}
        <Route path="/admin/na-registry" element={<ProtectedRoute><NaRegistry /></ProtectedRoute>} />
        <Route path="/admin/customs-lookups/:table" element={<ProtectedRoute requiredRole={['ADMIN']}><AdminPage><CustomsLookups /></AdminPage></ProtectedRoute>} />
        <Route path="/admin/goods-locations" element={<ProtectedRoute><GoodsLocations /></ProtectedRoute>} />
        <Route path="/customs/poa/pending" element={<ProtectedRoute requiredRole={['ADMIN', 'OPERATIONS']}><PendingReview /></ProtectedRoute>} />
        <Route path="/portal/poa" element={<ProtectedRoute requiredRole={["CLIENT"]}><POACheck /></ProtectedRoute>} />
      </Routes>
    </div>
  )
}

function App() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  )
}

export default App
